import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TrendingUp, Activity, BarChart3, Volume2, ChevronDown, ChevronUp, ExternalLink, Clock, AlertCircle, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OpportunityCard } from '@/components/daily-opportunities/OpportunityCard';
import { PastPerformance } from '@/components/daily-opportunities/PastPerformance';
import { HowItWorks } from '@/components/daily-opportunities/HowItWorks';
import { useLivePrices } from '@/hooks/useLivePrices';

interface ScreeningResult {
  id: string;
  token_symbol: string;
  token_name: string;
  current_price: number;
  composite_score: number;
  ema_50: number | null;
  ema_200: number | null;
  ema_score: number | null;
  rsi_value: number | null;
  rsi_score: number | null;
  macd_score: number | null;
  macd_crossover_signal: string | null;
  volume_increase_pct: number | null;
  volume_score: number | null;
  trend_strength: string | null;
  passes_all_criteria: boolean | null;
  screening_date: string;
  coingecko_id: string | null;
}

const DailyOpportunities = () => {
  const [results, setResults] = useState<ScreeningResult[]>([]);
  const [watchlist, setWatchlist] = useState<ScreeningResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [screeningDate, setScreeningDate] = useState<string | null>(null);
  const [isFresh, setIsFresh] = useState(false);
  const { prices } = useLivePrices();

  useEffect(() => {
    fetchScreeningResults();
  }, []);

  const fetchScreeningResults = async () => {
    setLoading(true);

    // Get the latest screening date
    const { data: latest } = await supabase
      .from('screening_results')
      .select('screening_date')
      .order('screening_date', { ascending: false })
      .limit(1)
      .single();

    if (!latest) {
      setLoading(false);
      return;
    }

    const date = latest.screening_date;
    setScreeningDate(date);

    // Check freshness (< 26 hours old)
    const screenDate = new Date(date);
    const hoursAgo = (Date.now() - screenDate.getTime()) / (1000 * 60 * 60);
    setIsFresh(hoursAgo < 26);

    // Fetch all results for that date
    const { data } = await supabase
      .from('screening_results')
      .select('*')
      .eq('screening_date', date)
      .order('composite_score', { ascending: false });

    if (data) {
      setResults(data.filter((r) => r.passes_all_criteria).slice(0, 10));
      setWatchlist(data.filter((r) => !r.passes_all_criteria).slice(0, 5));
    }

    setLoading(false);
  };

  const formattedDate = screeningDate
    ? new Date(screeningDate).toLocaleDateString('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-card/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary">
            GoToCoin
          </Link>
          <div className="flex gap-2">
            <Link to="/how-toss-works">
              <Button variant="ghost" size="sm">How TOSS Works</Button>
            </Link>
            <Link to="/opportunities">
              <Button variant="ghost" size="sm">TOSS Scores</Button>
            </Link>
            <Link to="/coin-reviewer">
              <Button variant="ghost" size="sm">Coin Reviewer</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Zap className="w-8 h-8 text-primary" />
            <h1 className="text-3xl md:text-4xl font-bold">Today's Top Opportunities</h1>
          </div>
          <p className="text-muted-foreground text-lg mb-4">
            Tokens meeting all four criteria: <span className="text-success">Trend ✓</span>{' '}
            <span className="text-success">Momentum ✓</span>{' '}
            <span className="text-success">Volume ✓</span>{' '}
            <span className="text-success">RSI ✓</span>
          </p>

          {screeningDate && (
            <div className="flex items-center justify-center gap-2 text-sm">
              <span
                className={`w-2.5 h-2.5 rounded-full ${isFresh ? 'bg-success animate-pulse' : 'bg-warning'}`}
              />
              <span className="text-muted-foreground">
                Screened: {formattedDate}
              </span>
              {!isFresh && (
                <Badge variant="outline" className="text-warning border-warning/30 text-xs">
                  <Clock className="w-3 h-3 mr-1" /> Stale
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse bg-card border-border">
                <CardContent className="p-6 h-48" />
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && results.length === 0 && (
          <Card className="bg-card border-border text-center py-16">
            <CardContent>
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No tokens met all four criteria today</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                The screener is selective by design. Check back tomorrow, or view the watchlist below for tokens that are close.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Opportunity Cards */}
        {!loading && results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {results.map((result, index) => (
              <OpportunityCard key={result.id} result={result} rank={index + 1} livePrice={prices.get(result.token_symbol.toUpperCase())?.price} />
            ))}
          </div>
        )}

        {/* Watchlist */}
        {!loading && watchlist.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-warning" />
              Watchlist — Close to Qualifying
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {watchlist.map((r) => (
                <Card key={r.id} className="bg-card border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-semibold">{r.token_symbol}</span>
                        <span className="text-muted-foreground text-sm ml-2">{r.token_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {r.composite_score}/100
                      </Badge>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {(r.ema_score ?? 0) > 0 && <Badge className="bg-success/20 text-success text-xs">Trend ✓</Badge>}
                      {(r.macd_score ?? 0) > 0 && <Badge className="bg-success/20 text-success text-xs">MACD ✓</Badge>}
                      {(r.rsi_score ?? 0) > 0 && <Badge className="bg-success/20 text-success text-xs">RSI ✓</Badge>}
                      {(r.volume_score ?? 0) > 0 && <Badge className="bg-success/20 text-success text-xs">Vol ✓</Badge>}
                      {(r.ema_score ?? 0) === 0 && <Badge className="bg-destructive/20 text-destructive text-xs">Trend ✗</Badge>}
                      {(r.macd_score ?? 0) === 0 && <Badge className="bg-destructive/20 text-destructive text-xs">MACD ✗</Badge>}
                      {(r.rsi_score ?? 0) === 0 && <Badge className="bg-destructive/20 text-destructive text-xs">RSI ✗</Badge>}
                      {(r.volume_score ?? 0) === 0 && <Badge className="bg-destructive/20 text-destructive text-xs">Vol ✗</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Past Performance */}
        {!loading && <PastPerformance />}

        {/* How It Works */}
        <HowItWorks />

        {/* Disclaimer */}
        <div className="mt-10 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          <p>
            This screener identifies technical setups, not financial advice. Always do your own research.
            Past performance does not indicate future results. Crypto is volatile — never invest more than you can afford to lose.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyOpportunities;
